import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Glitch from 'glitch-javascript-sdk';
import Footer from '../../component/layout/footer';
import { Alert, Button, Form, Collapse } from 'react-bootstrap';
import Danger from '../../../marketing/component/alerts/Danger';
import timeouts from '../../../../constants/timeouts';

const NewsletterStandalonePage = () => {
    const [newsletter, setNewsletter] = useState({});
    const [data, setData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        game_dev_updates: true,
        patches: true,
        event_announcements: true,
        exclusive_content_offers: true,
        community_highlights: true,
        upcoming_games_releases: true,
        strategy_gameplay_tips: true,
        industry_news_trends: true,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [showCategories, setShowCategories] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        Glitch.api.Communities.viewNewsletter(Glitch.util.Storage.get('community_id'), id)
            .then(response => {
                setNewsletter(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching newsletter:', error);
            });
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        setData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSignup = () => {
        setIsLoading(true);
        setErrors({});
        setSuccessMessage('');

        Glitch.api.Communities.registerNewsletterSubscriber(Glitch.util.Storage.get('community_id'), id, data)
            .then(response => {
                setIsLoading(false);
                setSuccessMessage("<h4 class='text-black'>Last Step: Confirm Subscription</h4><p>You have successfully signed up! Please check your inbox to confirm your subscription. If you do not see an email, please check your spam folder and unmark the email as spam.</p>");
                setIsSubscribed(true);
            })
            .catch(error => {
                setIsLoading(false);
                let jsonErrors = error?.response?.data;
                if (jsonErrors) {
                    setErrors(jsonErrors);
                    setTimeout(() => {
                        setErrors({});
                    }, timeouts.error_message_timeout);
                } else {
                    setErrors(error.response?.data?.errors || { general: 'An error occurred during subscription.' });
                }
            });
    };

    const capitalizeWords = (str) =>
        str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

    return (
        <Fragment>
            {/* Hero / Marketing Section */}
            <div className="bg-light py-5 mb-4">
                <div className="container text-center">
                    <h1 className="display-4 fw-bold text-black">{newsletter.name || 'Loading...'}</h1>
                    {newsletter.description && (
                        <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
                            {newsletter.description}
                        </p>
                    )}
                </div>
            </div>

            <div className="container mb-5">
                <div className="row justify-content-center">
                    <div className="col-lg-7">
                        <div className="card shadow-sm border-0">
                            {!isSubscribed && newsletter.banner_image && (
                                <div
                                    className="card-img-top text-center p-3 bg-dark"
                                    style={{ borderRadius: '0' }}
                                >
                                    <img
                                        src={newsletter.banner_image}
                                        alt="Newsletter Banner"
                                        className="img-fluid rounded"
                                        style={{ maxHeight: '200px' }}
                                    />
                                </div>
                            )}
                            <div className="card-body">
                                {/* Incentives Section */}
                                {!isSubscribed && newsletter.incentives && (
                                    <div className="mb-4 p-3 bg-white border rounded" style={{ background: '#f9f9f9' }}>
                                        <h4 className="fw-bold mb-3 text-black"><i className="fas fa-gift me-2 text-success"></i>Why Join?</h4>
                                        <div
                                            className="incentives-content"
                                            dangerouslySetInnerHTML={{ __html: newsletter.incentives }}
                                        />
                                    </div>
                                )}

                                {successMessage && (
                                    <Alert variant="success" className="my-3"><div
                                    className='text-center text-black'
                                    dangerouslySetInnerHTML={{ __html: successMessage }}
                                /></Alert>
                                )}

                                {!isSubscribed && (
                                    <Form className="mt-4">
                                        <h5 className="fw-bold mb-3 text-black text-center">Sign Up Now</h5>
                                        <Form.Group controlId="firstName" className="mb-3">
                                            <Form.Label><i className="fas fa-user me-2"></i>First Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                value={data.first_name}
                                                onChange={handleInputChange}
                                                placeholder="Enter your first name"
                                            />
                                            {errors.first_name && (
                                                <Form.Text className="text-danger">{errors.first_name[0]}</Form.Text>
                                            )}
                                        </Form.Group>

                                        <Form.Group controlId="lastName" className="mb-3">
                                            <Form.Label><i className="fas fa-user me-2"></i>Last Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                value={data.last_name}
                                                onChange={handleInputChange}
                                                placeholder="Enter your last name"
                                            />
                                            {errors.last_name && (
                                                <Form.Text className="text-danger">{errors.last_name[0]}</Form.Text>
                                            )}
                                        </Form.Group>

                                        <Form.Group controlId="email" className="mb-3">
                                            <Form.Label><i className="fas fa-envelope me-2"></i>Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={data.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter your email address"
                                            />
                                            {errors.email && (
                                                <Form.Text className="text-danger">{errors.email[0]}</Form.Text>
                                            )}
                                        </Form.Group>

                                        <div className="mb-3">
                                            <Button
                                                variant="link"
                                                onClick={() => setShowCategories(!showCategories)}
                                                aria-expanded={showCategories}
                                                className="p-0 text-primary"
                                            >
                                                <i className={`fas fa-${showCategories ? 'minus' : 'plus'}-circle me-1`}></i>
                                                {showCategories ? 'Hide Categories' : 'Choose Categories'}
                                            </Button>
                                        </div>

                                        <Collapse in={showCategories}>
                                            <div className="mb-3">
                                                <p className="fw-bold">Select which categories you’d like to receive:</p>
                                                <Form.Group controlId="categories">
                                                    {Object.keys(data).slice(3).map((category, idx) => (
                                                        <Form.Check
                                                            key={idx}
                                                            type="checkbox"
                                                            label={capitalizeWords(category)}
                                                            name={category}
                                                            checked={data[category]}
                                                            onChange={handleInputChange}
                                                            className="mb-2"
                                                        />
                                                    ))}
                                                </Form.Group>
                                            </div>
                                        </Collapse>

                                        <div className="mt-2 text-center">
                                            {Object.keys(errors).length > 0 &&
                                                Object.keys(errors).map((errorKey) => {
                                                    const errorValue = errors[errorKey];

                                                    // Check if the error value is an array
                                                    if (Array.isArray(errorValue)) {
                                                        return errorValue.map((message, index) => (
                                                            <Danger key={`${errorKey}-${index}`} message={message} />
                                                        ));
                                                    }

                                                    // If it's not an array (e.g., a string), render it directly
                                                    return (
                                                        <Danger key={errorKey} message={errorValue} />
                                                    );
                                                })}

                                        </div>

                                        <Button
                                            variant="primary"
                                            onClick={handleSignup}
                                            disabled={isLoading}
                                            className="w-100 mt-3"
                                            size="lg"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Signing up...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane me-2"></i> Submit
                                                </>
                                            )}
                                        </Button>
                                    </Form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </Fragment>
    );
};

export default NewsletterStandalonePage;
